import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePortalNavigate, usePortalPath } from '@/hooks/usePortalPath';
import {
  actionAuthSubmitButtonClassName,
  actionFormInputClassName,
  actionLinkClassName,
} from '@core/theme/actionTheme.util';
import { AuthHeader } from '@/components/auth/AuthHeader';

const SetNewPassword = () => {
  const location = useLocation();
  const portalNavigate = usePortalNavigate();
  const { path: portalPath } = usePortalPath();
  const { confirmSignIn } = useAuth();
  const email: string = location.state?.email;
  const [formState, setFormState] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Redirect to login if email is not provided
  useEffect(() => {
    if (!email) {
      portalNavigate('/login', {
        state: { message: 'Please sign in to set a new password' },
      });
    }
  }, [email, portalNavigate]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Validation
      if (formState.newPassword.length < 8) {
        setError('Password must be at least 8 characters long');
        setIsLoading(false);
        return;
      }

      if (formState.newPassword !== formState.confirmPassword) {
        setError('Passwords do not match');
        setIsLoading(false);
        return;
      }

      // Confirm sign in with new password
      const result = await confirmSignIn(formState.newPassword);

      if (result.success) {
        // Navigate to home after successful password change
        portalNavigate('/jobs', { replace: true });
      } else {
        setError('Failed to set new password. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white">
      <div className="w-full max-w-md px-6 py-8">
        <AuthHeader
          title="Set New Password"
          subtitle="Please set a new password for your account"
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700"
              >
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                required
                minLength={8}
                className={actionFormInputClassName}
                placeholder="Enter new password (min. 8 characters)"
                value={formState.newPassword}
                onChange={(e) =>
                  setFormState({ ...formState, newPassword: e.target.value })
                }
              />
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                minLength={8}
                className={actionFormInputClassName}
                placeholder="Confirm new password"
                value={formState.confirmPassword}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    confirmPassword: e.target.value,
                  })
                }
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={`${actionAuthSubmitButtonClassName} disabled:opacity-50`}
          >
            {isLoading ? 'Setting password...' : 'Set New Password'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Remember your password?{' '}
            <button
              onClick={() => portalNavigate('/login')}
              className={`font-medium ${actionLinkClassName}`}
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetNewPassword;
